import { useEffect, useId, useState } from "react";

import useClinicNotifications, {
  CSVCompletionNotificationType,
  CSVErrorNotificationType,
  ExcelCompletionNotificationType
} from "hooks/use-clinic-notifications";
import { CSVReportResponse } from "services/types/reports.type";

interface CsvReportHookProps {
  currentFilters: Record<string, any> | undefined;
  // Opt-in, so every existing caller behaves exactly as before. When on, the backend's
  // `csv.error.notification` for this report sets `csvReportFailed` - without it a failed export
  // leaves a "Preparing" banner that never resolves.
  trackErrors?: boolean;
}

type CSVReportNotificationMessage = {
  provider_id?: string;
  csv_download_url?: string;
  csv_title?: string;
};

export const useCsvReportHook = ({
  currentFilters,
  trackErrors = false
}: CsvReportHookProps) => {
  const [csvReport, setCsvReport] = useState<CSVReportResponse | null>(null);
  const [csvReportIsReady, setCsvReportIsReady] = useState(false);
  const [csvErrorMessages, setCsvErrorMessages] = useState<
    CSVReportNotificationMessage[]
  >([]);
  const errorHandlerKey = useId();

  const { systemMessages, addMessageHandlers, removeMessageHandlers } =
    useClinicNotifications();
  useEffect(() => {
    let isReady = false;

    if (csvReport) {
      if ("csv_title" in csvReport) {
        const csvNotifications = systemMessages
          .filter(({ type }) => type === CSVCompletionNotificationType)
          .map(({ message }) => message);

        isReady =
          csvNotifications.some(
            ({ provider_id, csv_download_url, csv_title }) =>
              provider_id === csvReport.provider_id &&
              csv_download_url === csvReport.csv_download_url &&
              csv_title === csvReport.csv_title
          ) || false;
      } else {
        const excelNotifications = systemMessages
          .filter(({ type }) => type === ExcelCompletionNotificationType)
          .map(({ message }) => message);

        isReady =
          excelNotifications.some(
            ({ provider_id, xlsx_download_url, xlsx_title }) =>
              provider_id === csvReport.provider_id &&
              xlsx_download_url === csvReport.xlsx_download_url &&
              xlsx_title === csvReport.xlsx_title
          ) || false;
      }

      setCsvReportIsReady(isReady);
    }

    setCsvReportIsReady(isReady);
  }, [csvReport, systemMessages]);

  // Collected from mount rather than per report: a task that fails immediately can send its
  // error before the 202 that names the report has come back.
  useEffect(() => {
    if (!trackErrors) {
      return undefined;
    }
    addMessageHandlers(
      (event: { type?: string; message?: CSVReportNotificationMessage }) => {
        if (event?.type === CSVErrorNotificationType && event.message) {
          setCsvErrorMessages((prev) => prev.concat(event.message ?? {}));
        }
      },
      errorHandlerKey
    );
    return () => removeMessageHandlers(errorHandlerKey);
  }, [trackErrors, addMessageHandlers, removeMessageHandlers, errorHandlerKey]);

  const csvReportFailed = Boolean(
    trackErrors &&
      csvReport &&
      "csv_title" in csvReport &&
      csvErrorMessages.some(
        ({ provider_id, csv_download_url, csv_title }) =>
          provider_id === csvReport.provider_id &&
          csv_download_url === csvReport.csv_download_url &&
          csv_title === csvReport.csv_title
      )
  );

  useEffect(() => {
    setCsvReportIsReady(false);
    setCsvReport(null);
  }, [currentFilters]);

  return { csvReport, csvReportIsReady, setCsvReport, csvReportFailed };
};
